/**
 * Phase 3 favorites merge — client trigger, marker, and edge call.
 */
import type { Session } from '@supabase/supabase-js';
import { readGrandfatherCount } from '@/lib/favoritesMigration';
import { mergeFavorites as mergeFavoritesRequest, type FavoritesMergeResult } from '@/lib/favoritesApi';

export const MERGE_MARKER_KEY = 'favorites_merge_done_v1';

export type Phase3MergeSuccess = Extract<FavoritesMergeResult, { ok: true }>;
export type Phase3MergeFailure = Extract<FavoritesMergeResult, { ok: false }>;
export type Phase3MergeResult = FavoritesMergeResult;

function mergeMarkerValue(anonUserId: string, userId: string): string {
  return `${anonUserId}:${userId}`;
}

export function isMergeComplete(anonUserId: string, userId: string): boolean {
  try {
    const stored = localStorage.getItem(MERGE_MARKER_KEY);
    return stored === mergeMarkerValue(anonUserId, userId);
  } catch {
    return false;
  }
}

export function hasMergeMarker(): boolean {
  try {
    return localStorage.getItem(MERGE_MARKER_KEY) !== null;
  } catch {
    return false;
  }
}

export function markMergeComplete(anonUserId: string, userId: string): void {
  try {
    localStorage.setItem(MERGE_MARKER_KEY, mergeMarkerValue(anonUserId, userId));
  } catch {
    /* ignore quota / private mode */
  }
}

export async function runPhase3Merge(
  session: Session,
  anonUserId: string,
): Promise<Phase3MergeResult> {
  if (!session.access_token) {
    console.warn('[favoritesMerge] merge skipped: missing access token');
    return { ok: false, error: { code: 'NO_TOKEN', message: 'Missing access token' } };
  }

  const result = await mergeFavoritesRequest(
    session,
    anonUserId,
    { grandfatherCount: readGrandfatherCount() },
  );

  if (!result.ok) {
    if (result.error?.code === 'NO_TOKEN') {
      console.warn('[favoritesMerge] merge skipped: missing access token');
    } else if (result.error?.message?.startsWith('Invalid merge response')) {
      console.warn('[favoritesMerge] merge failed: invalid response');
    } else if (result.error) {
      console.warn('[favoritesMerge] merge rejected', { code: result.error.code });
    }
  }

  return result;
}
